import moment, { Moment } from 'moment'

export const CURRENCY_CODE_BY_LOCALE = {
	'en-US': 'USD',
	'en-NZ': 'NZD',
}

export type LocaleCode = keyof typeof CURRENCY_CODE_BY_LOCALE

export function localeCodeToCurrencyCode( localeCode: LocaleCode ) {
	return CURRENCY_CODE_BY_LOCALE[ localeCode ] ?? CURRENCY_CODE_BY_LOCALE[ 'en-US' ]
}

export function formatCurrency( value: string, localeCode: LocaleCode = 'en-US' ) {
	const newValue = parseFloat( value )
	const currency = newValue
		.toLocaleString( localeCode, { style: 'currency', currency: localeCodeToCurrencyCode( localeCode ) } )

	return currency
}

export function formatMoney( money?: { amount: number, currencyCode: string } ) {
	return money?.amount ? new Intl.NumberFormat( 'en-US', {
		style: 'currency',
		currency: money?.currencyCode ?? 'USD',
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	} ).format( money?.amount ) : ''
}

export const APP_HEADER_HEIGHT = 56

export const TABLE_HEIGHT = 'calc( 100vh - 325px )'

export const GROUP_TABLE_HEIGHT = 'calc( 100vh - 400px )'

export const REQUESTS_TABLE_HEIGHT = 'calc( 100vh - 260px )'

export type CostComparison = 'high' | 'low' | 'normal'

export function compareCost( enteredCost: number, expectedCost?: number ): CostComparison {
	if ( expectedCost ) {
		const tenPercent = expectedCost * 0.1

		if ( enteredCost < expectedCost - tenPercent ) {
			return 'low'
		}

		if ( enteredCost > expectedCost + tenPercent ) {
			return 'high'
		}
	}

	return 'normal'
}

export function escapeRegExp( literal_string: string ) {
	return literal_string.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' )
}

/**
 * Used for EUI date pickers so that you can set max/min times (cant set one without the other).
 * This function will set the max time as current time if the selected date is the current date, otherwise
 * it will set it to the beginning of the day. This is useful to not allow the user to select a time in the future
 * while still allowing all times for dates in the past.
 *
 * @param selectedDate - Moment object of a selected date picker date, to decide if the start of the day should be the
 * current time (if the selected date is today) or allow any time of day (dates before current date)
 */
export const findMaxTime = ( selectedDate: Moment ): Moment => {
	return moment().startOf( 'day' ).isSameOrBefore( selectedDate ) ? moment() : moment().endOf( 'day' )
}
